import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const MIME_TYPES = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp8,opus',
  'video/webm',
]

export function getRecordingMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null
  return MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type)) ?? null
}

interface Props {
  maxBytes: number
  onFile: (file: File, interrupted: boolean, capacityReached?: boolean) => void
  onCancel: () => void
  onRecordingChange: (recording: boolean) => void
  onUnavailable: () => void
}

export function ExplainVideoCapture({ maxBytes, onFile, onCancel, onRecordingChange, onUnavailable }: Props) {
  const { t } = useTranslation('video')
  const [ready, setReady] = useState(false)
  const [recording, setRecording] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [interrupted, setInterrupted] = useState(false)
  const [settings, setSettings] = useState<MediaTrackSettings | null>(null)
  const liveRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const bytesRef = useRef(0)
  const interruptedRef = useRef(false)
  const capacityReachedRef = useRef(false)
  const mountedRef = useRef(true)
  const onFileRef = useRef(onFile)
  const recordingChangeRef = useRef(onRecordingChange)
  const unavailableRef = useRef(onUnavailable)
  onFileRef.current = onFile
  recordingChangeRef.current = onRecordingChange
  unavailableRef.current = onUnavailable

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop())
    streamRef.current = null
  }, [])

  useEffect(() => {
    mountedRef.current = true
    let disposed = false
    const openCamera = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia || !getRecordingMimeType()) throw new Error('unsupported')
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920, max: 1920 }, height: { ideal: 1080, max: 1080 }, frameRate: { ideal: 30, max: 30 } },
          audio: true,
        })
        if (disposed) { stream.getTracks().forEach(track => track.stop()); return }
        streamRef.current = stream
        const actual = stream.getVideoTracks()[0]?.getSettings()
        if (!actual || (actual.width != null && actual.height != null && (Math.max(actual.width, actual.height) > 1920 || Math.min(actual.width, actual.height) > 1080)) || (actual.frameRate != null && actual.frameRate > 30.1)) {
          stopTracks()
          throw new Error('capture-settings')
        }
        setSettings(actual)
        if (liveRef.current) liveRef.current.srcObject = stream
        setReady(true)
      } catch {
        if (!disposed) unavailableRef.current()
      }
    }
    void openCamera()
    const stopForBackground = () => {
      if (document.visibilityState === 'hidden' && recorderRef.current?.state === 'recording') {
        setInterrupted(true)
        interruptedRef.current = true
        recorderRef.current.stop()
      }
    }
    document.addEventListener('visibilitychange', stopForBackground)
    return () => {
      disposed = true
      mountedRef.current = false
      document.removeEventListener('visibilitychange', stopForBackground)
      const recorder = recorderRef.current
      if (recorder && recorder.state !== 'inactive') recorder.stop()
      stopTracks()
      recordingChangeRef.current(false)
    }
  }, [stopTracks, t])

  const start = () => {
    const stream = streamRef.current
    const mimeType = getRecordingMimeType()
    if (!stream || !mimeType) return
    try {
      const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 4_000_000, audioBitsPerSecond: 128_000 })
      chunksRef.current = []
      bytesRef.current = 0
      interruptedRef.current = false
      capacityReachedRef.current = false
      recorderRef.current = recorder
      recorder.ondataavailable = event => {
        if (event.data.size === 0) return
        chunksRef.current.push(event.data)
        bytesRef.current += event.data.size
        // A storage guard, not a time limit; retain what was captured and explain the interruption.
        // Leave room for the recorder's final data/footer. Never trim encoded chunks.
        if (bytesRef.current >= Math.max(1, Math.floor(maxBytes * 0.95)) && recorder.state === 'recording') {
          setError(t('recordingCapacity'))
          interruptedRef.current = true
          capacityReachedRef.current = true
          recorder.stop()
        }
      }
      recorder.onstop = () => {
        stopTracks()
        recordingChangeRef.current(false)
        if (!mountedRef.current) return
        setRecording(false)
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType })
        chunksRef.current = []
        if (blob.size === 0) { setError(t('emptyRecording')); return }
        const extension = recorder.mimeType.startsWith('video/mp4') ? 'mp4' : 'webm'
        const file = new File([blob], `buildup-${Date.now()}.${extension}`, { type: blob.type, lastModified: Date.now() })
        if (capacityReachedRef.current) onFileRef.current(file, interruptedRef.current, true)
        else onFileRef.current(file, interruptedRef.current)
      }
      recorder.onerror = () => {
        if (mountedRef.current) setError(t('recordingInterrupted'))
        interruptedRef.current = true
        if (recorder.state !== 'inactive') recorder.stop()
      }
      recorder.start(1000)
      setRecording(true)
      onRecordingChange(true)
    } catch {
      stopTracks()
      unavailableRef.current()
    }
  }

  return <section aria-label={t('capture')} className="space-y-3 rounded-xl border border-gray-200 bg-white p-3">
    <video ref={liveRef} autoPlay muted playsInline className="aspect-video w-full rounded-lg bg-black" aria-label={t('cameraPreview')} />
    {settings ? <p className="text-xs text-gray-500">{t('captureSettings', { width: settings.width ?? '—', height: settings.height ?? '—', fps: Math.round(settings.frameRate ?? 0) })}</p> : null}
    <p className="text-xs text-gray-600">{t('captureHint')}</p>
    {error ? <p role="alert" className="text-sm text-red-600">{error}</p> : null}
    {interrupted ? <p role="status" className="text-sm text-gray-600">{t('recordingInterrupted')}</p> : null}
    <div className="flex gap-3">
      <button type="button" disabled={!ready} onClick={recording ? () => recorderRef.current?.stop() : start} className="min-h-11 flex-1 rounded-lg bg-primary-600 px-3 py-2 font-semibold text-white disabled:bg-gray-300">
        {recording ? t('stopRecording') : t('startRecording')}
      </button>
      <button type="button" onClick={onCancel} disabled={recording} className="min-h-11 rounded-lg border border-gray-300 px-3 py-2 disabled:opacity-50">{t('cancel')}</button>
    </div>
  </section>
}
